/**
 * Nutrition-table extraction from OCR text (Lithuanian + English labels).
 *
 * OCR of a real package is hostile: diacritics vanish or mangle (ė→e, but also
 * ė→8), 1 and l and I are interchangeable, "g" reads as "9", table cells merge
 * onto one line, and label phrases break across lines mid-word. Every rule here
 * exists because a realistic sample broke a simpler version of it — see
 * nutrition-ocr.test.ts, which holds the corpus.
 */

export type NutrientField =
  | 'energy'
  | 'fat'
  | 'satFat'
  | 'transFat'
  | 'carbs'
  | 'sugars'
  | 'fiber'
  | 'protein'
  | 'salt'
  | 'sodium'

export interface ParsedValue {
  value: number
  /** The label declared "<0,5 g" rather than a measured amount. */
  lessThan: boolean
  /** Computed rather than read (salt from sodium), so review can flag it. */
  derived?: boolean
}

export interface ExtractedNutrition {
  /** One entry per column found on the row: [per 100 g, per portion]. */
  fields: Partial<Record<Exclude<NutrientField, 'energy'>, ParsedValue[]>>
  energyKj?: number
  energyKcal?: number
  /** Lines that held a value we could not attribute — shown for manual fixing. */
  unassigned: string[]
  /** Which column index the per-100 g values sit in, when headers were found. */
  per100gColumn: number | null
}

/**
 * Strip diacritics, lowercase, and collapse the punctuation OCR sprays between
 * a label and its value. Matching then happens on a predictable ASCII shape.
 */
export function normalizeOcrLines(raw: string): string[] {
  return raw
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[‹«˂≤]/g, '<')
    .replace(/[·•:;]+/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

/**
 * Repair digits inside a captured value only. Running this over a whole line
 * would destroy the unit ("g"→"9") and the label words. Input is already
 * lowercased, so `i` must map to 1 alongside `l` and `|`.
 */
export function fixDigits(token: string): string {
  return token
    .replace(/\s+/g, '')
    .replace(/[oO]/g, '0')
    .replace(/[lLiI|!]/g, '1')
    .replace(/[bB]/g, '8')
}

/**
 * Label patterns, tested in this order. Children come before parents so
 * "iš kurių sočiosios riebalų rūgštys" is saturated fat and not fat, and the
 * negative lookbehinds stop "mononesočiosios"/"polyunsaturates"/"trans fat"
 * from overwriting the row they merely contain.
 */
const LABELS: ReadonlyArray<readonly [NutrientField, RegExp]> = [
  ['transFat', /\btrans[\s-]*fat|transriebal/],
  ['satFat', /(?<![a-z])so[cč][il1|!](?:os[il1|!]os|uju?)|sot[il1|!]e[jl1|!]|(?<![a-z])saturat/],
  ['sugars', /cukr|su[g9]ars?/],
  ['fiber', /ska[il1|!]du[il1|!]|fib(?:re|er)/],
  [
    'energy',
    /energ[il1|!]n?[e3]?\s*vert[e3]?|energ[il1|!]ja|\benergy\b|energet[il1|!]c\s*va[il1|!]ue/,
  ],
  [
    'fat',
    /r[il1|!]e[b8]a[il1|!]a[il1|!]|(?<!(?:trans|saturated|unsaturated|saturates)[\s-])\bfat\b/,
  ],
  ['carbs', /an[g9][il1|!][il1|!]avanden|carb[o0]?-?\s*hydrat/],
  ['protein', /[b8]a[il1|!]tyma[il1|!]|prote[il1|!]n/],
  ['salt', /dru[s5]ka|\bsa[il1|!]t\b/],
  ['sodium', /natr[il1|!]|sodium/],
]

const PRIORITY = new Map<NutrientField, number>(LABELS.map(([name], i) => [name, i]))

// Digit classes admit the confusions OCR actually produces; fixDigits repairs them.
const D = '[0-9oOlIi|]'
const DG = '[0-9oOlIi|!bB]'

// The thousands alternative catches "1 674 kJ" as printed on many EU labels.
const ENERGY_KJ_SRC = `(?<lt><)?\\s*(?<val>${D}{1,2} ${D}{3}|${D}{2,5}(?:[.,]${D}{1,2})?)\\s*k[jl1i]\\b`
const ENERGY_KCAL_SRC = `(?<lt><)?\\s*(?<val>${D}{2,4}(?:[.,]${D}{1,2})?)\\s*kca[il1|!]`

const ENERGY_KJ = new RegExp(ENERGY_KJ_SRC)
const ENERGY_KCAL = new RegExp(ENERGY_KCAL_SRC)

/** Global twins, built per use for the same `lastIndex` reason as `gramValueRe`. */
const energyKjRe = (): RegExp => new RegExp(ENERGY_KJ_SRC, 'g')
const energyKcalRe = (): RegExp => new RegExp(ENERGY_KCAL_SRC, 'g')

/**
 * Built fresh per use on purpose: a shared /g regex carries `lastIndex` between
 * calls, so a stray `.test()` silently makes the next `matchAll()` skip matches.
 */
const gramValueRe = (): RegExp =>
  new RegExp(`(?<lt><)?\\s*(?<val>${DG}{1,3}(?:[.,]${DG}{1,2})?)\\s*(?<unit>[g9]\\b|mg\\b)`, 'g')

/** Column headers, tolerant of "1"→l/I and "0"→O misreads ("lOO g"). */
const HDR_100 = /(?<![0-9])[1li|!][o0][o0]\s*(?:g|ml)\b/
/**
 * The `per 30 g` alternative must not swallow `per 100 g`, which is the other
 * header entirely. It used to: "100" is three characters, so an English label
 * reading "Per 100 g   Per 30 g" matched the portion pattern at position 0 —
 * earlier than the 100 g header it had just eaten — and every value on the
 * label was then read out of the portion column.
 */
const HDR_PORT =
  /porcij|portion|serving|per\s+(?![1li|!][o0][o0]\s*(?:g|ml))[0-9oOlIi|]{1,3}\s*(?:g|ml)/

function parseGram(m: RegExpMatchArray): ParsedValue {
  const groups = m.groups ?? {}
  let value = Number.parseFloat(fixDigits(groups.val ?? '').replace(',', '.'))
  if (groups.unit === 'mg') value /= 1000
  return { value, lessThan: Boolean(groups.lt) }
}

function parseEnergy(m: RegExpMatchArray): number {
  return Number.parseFloat(fixDigits(m.groups?.val ?? '').replace(',', '.'))
}

/**
 * The figure standing in the per-100 g column, or nothing.
 *
 * When that column is the second one and the row only yielded a single value,
 * the missing cell is not filled from the first: on such a label the first cell
 * is the per-portion figure, and handing it over as per-100 g is a three-fold
 * error that no plausibility check can catch — both columns are internally
 * consistent. A field the review sheet shows empty gets typed in; a field it
 * shows wrongly filled gets saved.
 */
function valueInColumn<T>(values: readonly T[], per100gColumn: number | null): T | undefined {
  if (values.length === 0) return undefined
  return per100gColumn == null ? values[0] : values[per100gColumn]
}

/** Locate the per-100 g column by comparing header positions on the same line. */
function detectPer100gColumn(lines: readonly string[]): number | null {
  for (const line of lines.slice(0, 6)) {
    const h100 = line.match(HDR_100)
    const hPort = line.match(HDR_PORT)
    if (h100 && hPort) return (h100.index ?? 0) < (hPort.index ?? 0) ? 0 : 1
    if (h100) return 0
  }
  return null
}

/**
 * Parse a nutrition table out of raw OCR text.
 *
 * Three rules carry the parse beyond naive per-line matching:
 *  1. Segmentation — a line is split at every label it contains, so
 *     "Angliavandeniai 45,1 g iš kurių cukrūs 3,8 g" fills both fields.
 *  2. Energy fallback — a line with no label but a kJ/kcal value is energy,
 *     covering the common layout where the label sits on its own line.
 *  3. Pending-label carry — a label with no value lends itself to the next
 *     value-only line, covering phrases broken mid-word by a column break.
 */
export function extractNutrition(rawOcrText: string): ExtractedNutrition {
  const lines = normalizeOcrLines(rawOcrText)
  const fields: ExtractedNutrition['fields'] = {}
  const unassigned: string[] = []
  // Every figure on the energy row, in the order it is printed — the same shape
  // the gram fields keep, so the column is chosen for energy the same way. Only
  // taking the first one meant a portion-first label produced per-100 g macros
  // beside a per-portion kcal, which is a food that cannot exist.
  const kjValues: number[] = []
  const kcalValues: number[] = []
  let pending: NutrientField | null = null

  for (const line of lines) {
    let found: Array<{ field: NutrientField; index: number }> = []
    for (const [name, re] of LABELS) {
      const m = re.exec(line)
      if (m) found.push({ field: name, index: m.index })
    }
    found.sort(
      (a, b) => a.index - b.index || (PRIORITY.get(a.field) ?? 0) - (PRIORITY.get(b.field) ?? 0),
    )

    if (found.length === 0) {
      if (ENERGY_KJ.test(line) || ENERGY_KCAL.test(line)) {
        found = [{ field: 'energy', index: 0 }]
      } else if (pending) {
        found = [{ field: pending, index: 0 }]
      }
    }

    if (found.length === 0) {
      if ([...line.matchAll(gramValueRe())].length > 0) unassigned.push(line)
      pending = null
      continue
    }

    let gotValue = false
    for (let k = 0; k < found.length; k++) {
      const start = found[k]!.index
      const end = k + 1 < found.length ? found[k + 1]!.index : line.length
      const segment = line.slice(start, end)
      const field = found[k]!.field

      if (field === 'energy') {
        const kj = [...segment.matchAll(energyKjRe())]
        const kcal = [...segment.matchAll(energyKcalRe())]
        if (kj.length > 0) {
          kjValues.push(...kj.map(parseEnergy))
          gotValue = true
        }
        if (kcal.length > 0) {
          kcalValues.push(...kcal.map(parseEnergy))
          gotValue = true
        }
        continue
      }

      const values = [...segment.matchAll(gramValueRe())].map(parseGram)
      if (values.length > 0) {
        gotValue = true
        if (!(field in fields)) fields[field] = values
      }
    }

    pending = !gotValue && found.length === 1 ? found[0]!.field : null
  }

  // EU labels may declare sodium instead of salt; the conversion is fixed at 2.5.
  if (!fields.salt && fields.sodium?.[0]) {
    const sodium = fields.sodium[0]
    fields.salt = [
      { value: Number((sodium.value * 2.5).toFixed(2)), lessThan: sodium.lessThan, derived: true },
    ]
  }

  const per100gColumn = detectPer100gColumn(lines)
  let energyKj = valueInColumn(kjValues, per100gColumn)
  let energyKcal = valueInColumn(kcalValues, per100gColumn)
  if (energyKcal == null && energyKj != null) energyKcal = Math.round(energyKj / 4.184)
  if (energyKj == null && energyKcal != null) energyKj = Math.round(energyKcal * 4.184)

  return { fields, energyKj, energyKcal, unassigned, per100gColumn }
}

export interface NutritionCandidate {
  kcal: number | null
  protein: number | null
  carbs: number | null
  fat: number | null
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
}

/** Collapse a multi-column extraction into the per-100 g candidate to review. */
export function toPer100gCandidate(extracted: ExtractedNutrition): NutritionCandidate {
  const pick = (field: Exclude<NutrientField, 'energy'>): number | null => {
    const values = extracted.fields[field]
    if (!values) return null
    return valueInColumn(values, extracted.per100gColumn)?.value ?? null
  }
  return {
    kcal: extracted.energyKcal ?? null,
    protein: pick('protein'),
    carbs: pick('carbs'),
    fat: pick('fat'),
    sugars: pick('sugars'),
    satFat: pick('satFat'),
    fiber: pick('fiber'),
    salt: pick('salt'),
  }
}

export interface ConfidenceReport {
  /** 0..1 — below 0.45 the review screen forces field-by-field confirmation. */
  score: number
  /** Fields whose value failed a plausibility or hierarchy check. */
  suspect: Array<Exclude<NutrientField, 'energy'> | 'kcal'>
}

/** 1 = check passed, 0 = check failed, 0.5 = not enough data to run it. */
type CheckResult = 0 | 0.5 | 1

/**
 * Score an extraction so the review sheet knows what to highlight.
 *
 * A cross-check that cannot run scores neutral, never as a pass: a label where
 * only the energy row was read has nothing to contradict, and scoring that as
 * consistent would wave a near-empty extraction through. Failing a check never
 * discards the value — a wrong number the user can see and fix beats a missing
 * one they have to retype.
 */
export function scoreExtraction(
  candidate: NutritionCandidate,
  meanWordConfidence = 0,
): ConfidenceReport {
  const suspect: ConfidenceReport['suspect'] = []
  const { kcal, protein, carbs, fat, sugars, satFat, fiber, salt } = candidate

  const targets = [kcal, protein, carbs, fat, sugars, satFat, fiber, salt]
  const coverage = targets.filter((v) => v != null).length / targets.length

  const bounds = [
    ['kcal', kcal, 950],
    ['protein', protein, 100],
    ['carbs', carbs, 100],
    ['fat', fat, 100],
    ['sugars', sugars, 100],
    ['satFat', satFat, 100],
    ['fiber', fiber, 100],
    ['salt', salt, 25],
  ] as const
  let plausible: CheckResult = bounds.some(([, v]) => v != null) ? 1 : 0.5
  for (const [name, value, max] of bounds) {
    if (value != null && (value < 0 || value > max)) {
      plausible = 0
      suspect.push(name)
    }
  }

  let energyOk: CheckResult = 0.5
  if (kcal != null && protein != null && carbs != null && fat != null) {
    const atwater = protein * 4 + carbs * 4 + fat * 9
    energyOk = atwater > 0 && Math.abs(kcal - atwater) / atwater > 0.3 ? 0 : 1
    if (energyOk === 0) suspect.push('kcal')
  }

  const hasHierarchyData =
    (fat != null && satFat != null) || (carbs != null && sugars != null) || coverage > 0.5
  let hierarchy: CheckResult = hasHierarchyData ? 1 : 0.5
  if (fat != null && satFat != null && satFat > fat + 0.01) {
    hierarchy = 0
    suspect.push('satFat')
  }
  if (carbs != null && sugars != null && sugars > carbs + 0.01) {
    hierarchy = 0
    suspect.push('sugars')
  }
  const mass = (protein ?? 0) + (carbs ?? 0) + (fat ?? 0) + (fiber ?? 0) + (salt ?? 0)
  if (mass > 105) hierarchy = 0

  const checks = [plausible, energyOk, hierarchy]
  const consistency = checks.reduce<number>((a, b) => a + b, 0) / checks.length
  const evidence = 0.45 * coverage + 0.2 * Math.min(1, Math.max(0, meanWordConfidence / 100))
  // A definite contradiction is worse than a merely thin read, so it also
  // discounts whatever evidence there was.
  const penalty = checks.includes(0) ? 0.75 : 1

  const score = (evidence + 0.35 * consistency) * penalty
  return { score: Number(score.toFixed(3)), suspect: [...new Set(suspect)] }
}
